(() => {
  if (customElements.get('deep-format-selector')) return;
  const tiers = [
    { key: 'standard', signed: false, pr: false },
    { key: 'signed', signed: true, pr: false },
    { key: 'pr', signed: false, pr: true },
    { key: 'signed-pr', signed: true, pr: true },
  ];
  const classify = (variants) => variants.map((variant) => ({
    ...variant,
    binding: variant.title.includes('Hardcover') ? 'hardcover' : 'paperback',
    signed: variant.title.startsWith('Signed '),
    pr: variant.title.includes('PR Package'),
  }));
  customElements.define('deep-format-selector', class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      // Auto-selection must wait until Dawn can disable and update its purchase form.
      await Promise.all(['product-info', 'product-form', 'variant-selects'].map((name) => customElements.whenDefined(name)));
      if (!this.isConnected || this.initialized) return;
      this.productInfo = this.closest('product-info');
      const source = this.querySelector('[data-format-catalog]');
      if (!source || !this.productInfo || typeof subscribe !== 'function') return;
      try {
        const catalog = JSON.parse(source.textContent);
        this.variants = classify(catalog.variants);
        if (catalog.optionCount !== 1 || this.variants.length !== 8) return;
        for (const binding of ['hardcover', 'paperback']) {
          for (const tier of tiers) {
            const title = `${tier.signed ? 'Signed ' : ''}${binding === 'hardcover' ? 'Hardcover' : 'Paperback'}${tier.pr ? ' with PR Package' : ''}`;
            if (this.variants.filter((v) => v.title === title).length !== 1) return;
          }
        }
        this.selected = this.variants.find((v) => v.id === catalog.selectedId);
        if (!this.selected || !this.nativePicker()) return;
        const defaultHardcover = !new URLSearchParams(location.search).has('variant') && this.selected.id !== this.findEdition('hardcover', tiers[0]).id;
        if (defaultHardcover) this.selected = this.findEdition('hardcover', tiers[0]);
        const group = this.dataset.section;
        const panel = document.createElement('div');
        panel.className = 'deep-format-panel';
        panel.innerHTML = `<fieldset><legend>Choose your format</legend><div class="deep-format-options">${['hardcover', 'paperback'].map((binding) => `<label class="deep-format-card"><input type="radio" name="deep-binding-${group}" data-binding="${binding}" value="${binding}"><span><b>${binding === 'hardcover' ? 'Hardcover' : 'Paperback'}</b><small data-format-state="${binding}"></small></span></label>`).join('')}</div></fieldset><fieldset class="deep-edition-tiers"><legend>Choose your edition</legend>${tiers.map((tier) => `<label class="deep-edition-tier"><input type="radio" name="deep-tier-${group}" data-tier="${tier.key}" value="${tier.key}"><span><strong data-tier-name="${tier.key}"></strong>${tier.key === 'signed-pr' ? '<em class="deep-tier-popular" hidden>Most popular</em>' : ''}</span><small class="deep-tier-stock" data-tier-stock="${tier.key}" hidden>Sold out</small></label>`).join('')}</fieldset><p class="deep-edition-stock" role="status" hidden>This edition is sold out.</p>`;
        this.append(panel);
        this.render();
        this.addEventListener('change', this.onChange);
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== (this.productInfo.dataset.originalSection || this.dataset.section) || !data.variant) return;
          const fresh = data.html?.querySelector('deep-format-selector [data-format-catalog]');
          if (fresh) {
            try { this.variants = classify(JSON.parse(fresh.textContent).variants); } catch (_) { /* Keep current catalog if response data is missing. */ }
          }
          const variant = this.variants.find((v) => v.id === data.variant.id);
          if (!variant) return;
          Object.assign(variant, { available: data.variant.available });
          this.selected = variant;
          this.render();
        });
        this.initialized = true;
        this.hidden = false;
        this.dataset.ready = '';
        if (defaultHardcover) this.querySelector('[data-binding="hardcover"]').dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        this.querySelector('.deep-format-panel')?.remove();
        this.hidden = true;
        delete this.dataset.ready;
        console.error('The Commons format selector could not initialize.', error);
      }
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.removeEventListener('change', this.onChange);
      this.querySelector('.deep-format-panel')?.remove();
      this.initialized = false;
      this.hidden = true;
      delete this.dataset.ready;
    }
    nativePicker() {
      return [...this.productInfo.querySelectorAll('variant-selects')].find((picker) => picker.style.display !== 'none');
    }
    findEdition(binding, tier) {
      return this.variants.find((v) => v.binding === binding && v.signed === tier.signed && v.pr === tier.pr);
    }
    render() {
      const variant = this.selected;
      for (const input of this.querySelectorAll('[data-binding]')) input.checked = input.dataset.binding === variant.binding;
      for (const state of this.querySelectorAll('[data-format-state]')) state.textContent = state.dataset.formatState === variant.binding ? 'Selected' : 'Select format';
      for (const tier of tiers) {
        const edition = this.findEdition(variant.binding, tier);
        this.querySelector(`[data-tier="${tier.key}"]`).checked = edition.id === variant.id;
        this.querySelector(`[data-tier-name="${tier.key}"]`).textContent = edition.title;
        this.querySelector(`[data-tier-stock="${tier.key}"]`).hidden = edition.available;
      }
      this.querySelector('.deep-tier-popular').hidden = variant.binding !== 'hardcover';
      this.querySelector('.deep-edition-stock').hidden = variant.available;
    }
    onChange = (event) => {
      if (!event.target.matches('[data-binding], [data-tier]')) return;
      const binding = this.querySelector('[data-binding]:checked').value;
      const tier = tiers.find((item) => item.key === this.querySelector('[data-tier]:checked').value);
      const variant = this.findEdition(binding, tier);
      const picker = this.nativePicker();
      const native = picker && [...picker.querySelectorAll('input[type="radio"], option')].find((input) => input.value === variant?.title);
      if (!native) { this.render(); return; }
      this.selected = variant;
      this.render();
      // Dawn remains authoritative for requests, prices, inventory, cart IDs and URL state.
      if (native.tagName === 'OPTION') {
        native.parentElement.value = native.value;
        native.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        native.checked = true;
        native.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
  });
})();
